import axios from "axios";
import ResponseAlerts from "../functions/ResponseAlerts";
import { server } from "../config/config";

const BASE_LICENSE = "access-control-service/licenses";

const RESOURCES = {
   PAGINATED_LICENSE: (page, filters, orderBy, rowsPerPage) => {
      const params = new URLSearchParams({
         page: String(page),
         itemsPerPage: String(rowsPerPage),
         searchBy: filters.search ?? '',
         orderBy: orderBy.collumnTitle,
         orderDirection: orderBy.orderDirection,
      });
      Object.entries(filters).forEach(([key, value]) => {
         if (key !== "search" && value) {
            params.set(key, value);
         }
      });
      return `${BASE_LICENSE}/paginated?${params.toString()}`;
   },
   EXPORT_LICENSES_CSV: (filters) => {
      const params = new URLSearchParams({ searchBy: filters.search ?? '' });
      Object.entries(filters).forEach(([key, value]) => {
         if (key !== "search" && value) {
            params.set(key, value);
         }
      });
      return `${BASE_LICENSE}/export-csv?${params.toString()}`;
   },
   PAGINATED_LICENSE_BILLINGS: (licenseId, page, itemsPerPage, orderBy, orderDirection) => {
      const params = new URLSearchParams({
         page: String(page),
         itemsPerPage: String(itemsPerPage),
         orderBy,
         orderDirection,
      });
      return `${BASE_LICENSE}/${licenseId}/billings/paginated?${params.toString()}`;
   },
   PAGINATED_LICENSE_ASSOCIATIONS: (licenseId, page, itemsPerPage, orderBy, orderDirection) => {
      const params = new URLSearchParams({
         page: String(page),
         itemsPerPage: String(itemsPerPage),
         orderBy,
         orderDirection,
      });
      return `${BASE_LICENSE}/${licenseId}/associations/paginated?${params.toString()}`;
   },
   PAGINATED_LICENSE_HISTORY: (licenseId, page, itemsPerPage) => {
      const params = new URLSearchParams({
         page: String(page),
         itemsPerPage: String(itemsPerPage),
      });
      return `${BASE_LICENSE}/${licenseId}/history/paginated?${params.toString()}`;
   },
   PAGINATED_LICENSE_ACTIONS: (licenseId, page, itemsPerPage, orderBy, orderDirection) => {
      const params = new URLSearchParams({
         page: String(page),
         itemsPerPage: String(itemsPerPage),
         orderBy,
         orderDirection,
      });
      return `${BASE_LICENSE}/${licenseId}/actions/paginated?${params.toString()}`;
   },
   CREATE_LICENSE: () => `${BASE_LICENSE}`,
   UPDATE_LICENSE: (id) => `${BASE_LICENSE}/${id}`,
   DELETE_LICENSE: (id) => `${BASE_LICENSE}/${id}`,
   GET_LICENSE: (id) => `${BASE_LICENSE}/${id}`,

   PAGINATED_TYPES: (page, searchText, orderBy, includePrices = false) =>
      `${BASE_LICENSE}/types/paginated?page=${page}&itemsPerPage=8` +
      `&searchBy=${searchText}&orderBy=${orderBy.collumnTitle}&orderDirection=${orderBy.orderDirection}&includePrices=${includePrices}`,
   PAGINATED_TYPE_PRICES: (page, searchText, orderBy, typeId, period) =>
      `${BASE_LICENSE}/types/${typeId}/prices/paginated?page=${page}&itemsPerPage=8` +
      `&searchBy=${searchText}&orderBy=${orderBy.collumnTitle}&orderDirection=${orderBy.orderDirection}&period=${period}`,
   CREATE_TYPE: () => `${BASE_LICENSE}/types`,
   UPDATE_TYPE: (id) => `${BASE_LICENSE}/types/${id}`,
   DELETE_TYPE: (id) => `${BASE_LICENSE}/types/${id}`,
   GET_TYPES: () => `${BASE_LICENSE}/types`,
   GET_TYPE_FEATURES: (typeCode) => `${BASE_LICENSE}/types/${typeCode}/features`,
   GET_TYPE: (id) => `${BASE_LICENSE}/types/${id}`,

   PAGINATED_FEATURES: (page, searchText, orderBy, includePrices = false) =>
      `${BASE_LICENSE}/features/paginated?page=${page}&itemsPerPage=8` +
      `&searchBy=${searchText}&orderBy=${orderBy.collumnTitle}&orderDirection=${orderBy.orderDirection}&includePrices=${includePrices}`,
   PAGINATED_FEATURE_PRICES: (page, searchText, orderBy, featureId, period) =>
      `${BASE_LICENSE}/features/${featureId}/prices/paginated?page=${page}&itemsPerPage=8` +
      `&searchBy=${searchText}&orderBy=${orderBy.collumnTitle}&orderDirection=${orderBy.orderDirection}&period=${period}`,
   CREATE_FEATURE: () => `${BASE_LICENSE}/features`,
   UPDATE_FEATURE: (id) => `${BASE_LICENSE}/features/${id}`,
   DELETE_FEATURE: (id) => `${BASE_LICENSE}/features/${id}`,
   GET_FEATURES: () => `${BASE_LICENSE}/features`,
   GET_FEATURE: (id) => `${BASE_LICENSE}/features/${id}`,

   PAGINATED_DISCOUNT_REASONS: (page, searchText, orderBy) =>
      `${BASE_LICENSE}/discount-reasons/paginated?page=${page}&itemsPerPage=8` +
      `&searchBy=${searchText}&orderBy=${orderBy.collumnTitle}&orderDirection=${orderBy.orderDirection}`,
   CREATE_DISCOUNT_REASON: () => `${BASE_LICENSE}/discount-reasons`,
   UPDATE_DISCOUNT_REASON: (id) => `${BASE_LICENSE}/discount-reasons/${id}`,
   DELETE_DISCOUNT_REASON: (id) => `${BASE_LICENSE}/discount-reasons/${id}`,
   GET_DISCOUNT_REASON: (id) => `${BASE_LICENSE}/discount-reasons/${id}`,
   GET_DISCOUNT_REASONS: () => `${BASE_LICENSE}/discount-reasons`,

   CALCULATE_TOTAL_AMOUNT: (period, typeCode, featureCodes) => {
      const params = new URLSearchParams({
         period: String(period),
         typeCode: String(typeCode),
      });

      if (featureCodes?.length) {
         params.set('featureCodes', featureCodes.join(','));
      }

      return `${BASE_LICENSE}/calculate?${params.toString()}`;
   },

   UPDATE_SERIAL_NUMBER: (id) =>
      `${BASE_LICENSE}/${id}/serial-number`,

   TRANSFER_TO_UNIT: (id) =>
      `${BASE_LICENSE}/${id}/transfer`,

   UPDATE_ALLOCATION: (id) =>
      `${BASE_LICENSE}/${id}/allocate`,

   BLOCK: (id) =>
      `${BASE_LICENSE}/${id}/block`,

   GET_STATES_COUNT: () =>
      `${BASE_LICENSE}/states-count`,

   CREATE_BILLING: () =>
      `${BASE_LICENSE}/billings`,
   REGISTER_PAYMENT: (billingId) =>
      `${BASE_LICENSE}/billings/${billingId}/payment`,

   UPDATE_DUE_DATE: () =>
      `${BASE_LICENSE}/billings`,

   UPDATE_BINDING_CODE: (id) =>
      `${BASE_LICENSE}/${id}/binding-code`,

   UNBLOCK: (id) =>
      `${BASE_LICENSE}/${id}/unblock`,

   SUSPEND: (id) =>
      `${BASE_LICENSE}/${id}/suspend`,

   RENAME: (id) =>
      `${BASE_LICENSE}/${id}/rename`,

   CHANGE_LICENSE_FEATURES: (id) =>
      `${BASE_LICENSE}/${id}/features`,

   GET_ANALYTICS: () =>
      `${BASE_LICENSE}/analytics`,
};

class LicenseService {
   static async getPaginatedLicenses(page, filters, orderBy, rowsPerPage, signal = null) {
      try {
         return await axios.get(
            RESOURCES.PAGINATED_LICENSE(page, filters, orderBy, rowsPerPage),
            {
               signal,
            }
         );
      } catch (error) {
         console.log(error);
         ResponseAlerts({
            response: error.response,
            errorMessage:
               error.response?.data?.message ??
               "Erro ao listar licenças paginado",
         });
         return error.response;
      }
   }

   static async exportLicensesToCsv(filters = {}) {
      const token = localStorage.getItem("@BRVAuth:token");
      const response = await fetch(
         `${server.url}/${RESOURCES.EXPORT_LICENSES_CSV(filters)}`,
         {
            headers: {
               Authorization: `Bearer ${token}`,
            },
         },
      );

      if (!response.ok) {
         const errorData = await response.json().catch(() => ({}));
         ResponseAlerts({
            response: { status: response.status, data: errorData },
            errorMessage:
               errorData?.message ??
               "Erro ao exportar licenças para CSV",
         });
         return null;
      }

      return response;
   }

   static async getLicense(id) {
      try {
         return await axios.get(
            RESOURCES.GET_LICENSE(id),
         );
      } catch (error) {
         console.log(error);
         ResponseAlerts({
            response: error.response,
            errorMessage:
               error.response?.data?.message ??
               "Erro ao obter licença",
         });
         return error.response;
      }
   }

   static async getPaginatedLicenseBillings(
      licenseId,
      page,
      itemsPerPage,
      orderBy = "dueDate",
      orderDirection = "DESC",
   ) {
      try {
         return await axios.get(
            RESOURCES.PAGINATED_LICENSE_BILLINGS(
               licenseId,
               page,
               itemsPerPage,
               orderBy,
               orderDirection,
            ),
         );
      } catch (error) {
         console.log(error);
         ResponseAlerts({
            response: error.response,
            errorMessage:
               error.response?.data?.message ??
               "Erro ao listar cobranças da licença",
         });
         return error.response;
      }
   }

   static async getPaginatedLicenseAssociations(
      licenseId,
      page,
      itemsPerPage,
      orderBy = "createdAt",
      orderDirection = "DESC",
   ) {
      try {
         return await axios.get(
            RESOURCES.PAGINATED_LICENSE_ASSOCIATIONS(
               licenseId,
               page,
               itemsPerPage,
               orderBy,
               orderDirection,
            ),
         );
      } catch (error) {
         console.log(error);
         ResponseAlerts({
            response: error.response,
            errorMessage:
               error.response?.data?.message ??
               "Erro ao listar associações da licença",
         });
         return error.response;
      }
   }

   static async getPaginatedLicenseHistory(licenseId, page, itemsPerPage) {
      try {
         return await axios.get(
            RESOURCES.PAGINATED_LICENSE_HISTORY(licenseId, page, itemsPerPage),
         );
      } catch (error) {
         console.log(error);
         ResponseAlerts({
            response: error.response,
            errorMessage:
               error.response?.data?.message ??
               "Erro ao listar histórico da licença",
         });
         return error.response;
      }
   }

   static async getPaginatedLicenseActions(
      licenseId,
      page,
      itemsPerPage,
      orderBy = "createdAt",
      orderDirection = "DESC",
   ) {
      try {
         return await axios.get(
            RESOURCES.PAGINATED_LICENSE_ACTIONS(
               licenseId,
               page,
               itemsPerPage,
               orderBy,
               orderDirection,
            ),
         );
      } catch (error) {
         console.log(error);
         ResponseAlerts({
            response: error.response,
            errorMessage:
               error.response?.data?.message ??
               "Erro ao listar agendamentos da licença",
         });
         return error.response;
      }
   }

   static async createLicense(payload) {
      try {
         const response = await axios.post(
            RESOURCES.CREATE_LICENSE(),
            payload,
         );

         ResponseAlerts({
            response,
            successMessage: "Licença criada com sucesso",
         });

         return response;
      } catch (error) {
         console.log(error);
         ResponseAlerts({
            response: error.response,
            errorMessage:
               error.response?.data?.message ??
               "Erro ao criar licença",
         });
         return error.response;
      }
   }

   static async updateLicense(id, payload) {
      try {
         const response = await axios.put(
            RESOURCES.UPDATE_LICENSE(id),
            payload,
         );

         ResponseAlerts({
            response,
            successMessage: "Licença atualizada com sucesso",
         });

         return response;
      } catch (error) {
         console.log(error);
         ResponseAlerts({
            response: error.response,
            errorMessage:
               error.response?.data?.message ??
               "Erro ao atualizar licença",
         });
         return error.response;
      }
   }

   static async deleteLicense(id) {
      try {
         const response = await axios.delete(
            RESOURCES.DELETE_LICENSE(id),
         );

         ResponseAlerts({
            response,
            successMessage: "Licença removida com sucesso",
         });

         return response;
      } catch (error) {
         console.log(error);
         ResponseAlerts({
            response: error.response,
            errorMessage:
               error.response?.data?.message ??
               "Erro ao remover licença",
         });
         return error.response;
      }
   }

   static async calculateTotalAmount(period, typeCode, featureCodes) {
      try {
         return await axios.get(
            RESOURCES.CALCULATE_TOTAL_AMOUNT(period, typeCode, featureCodes),
         );
      } catch (error) {
         console.log(error);
         ResponseAlerts({
            response: error.response,
            errorMessage: error.response?.data?.message ?? "Erro ao calcular valor total",
         });
         return error.response;
      }
   }

   static async getPaginatedTypes(page, searchText, orderBy, includePrices = false) {
      try {
         return await axios.get(
            RESOURCES.PAGINATED_TYPES(page, searchText ?? '', orderBy, includePrices),
         );
      } catch (error) {
         console.log(error);
         ResponseAlerts({
            response: error.response,
            errorMessage: error.response?.data?.message ?? "Erro ao listar tipos paginado",
         });
         return error.response;
      }
   }

   static async getTypes() {
      try {
         return await axios.get(
            RESOURCES.GET_TYPES(),
         );
      } catch (error) {
         console.log(error);
         ResponseAlerts({
            response: error.response,
            errorMessage: error.response?.data?.message ?? "Erro ao listar tipos",
         });
         return error.response;
      }
   }

   static async getTypeFeatures(typeCode) {
      try {
         return await axios.get(
            RESOURCES.GET_TYPE_FEATURES(typeCode),
         );
      } catch (error) {
         console.log(error);
         ResponseAlerts({
            response: error.response,
            errorMessage: error.response?.data?.message ?? "Erro ao listar funcionalidades do tipo",
         });
         return error.response;
      }
   }

   static async getPaginatedTypePrices(page, searchText, orderBy, typeId, period) {
      try {
         return await axios.get(
            RESOURCES.PAGINATED_TYPE_PRICES(page, searchText ?? '', orderBy, typeId, period),
         );
      } catch (error) {
         console.log(error);
         ResponseAlerts({
            response: error.response,
            errorMessage: error.response?.data?.message ?? "Erro ao listar preços de tipos paginado",
         });
         return error.response;
      }
   }

   static async getType(id) {
      try {
         return await axios.get(
            RESOURCES.GET_TYPE(id),
         );
      } catch (error) {
         console.log(error);
         ResponseAlerts({
            response: error.response,
            errorMessage: error.response?.data?.message ?? "Erro ao obter tipo",
         });
         return error.response;
      }
   }

   static async createType(payload) {
      try {
         const response = await axios.post(RESOURCES.CREATE_TYPE(), payload);
         ResponseAlerts({
            response,
            successMessage: "Tipo de licença criado com sucesso",
         });
         return response;
      } catch (error) {
         console.log(error);
         ResponseAlerts({
            response: error.response,
            errorMessage: error.response?.data?.message ?? "Erro ao criar tipo",
         });
         return error.response;
      }
   }

   static async updateType(id, payload) {
      try {
         const response = await axios.put(RESOURCES.UPDATE_TYPE(id), payload);
         ResponseAlerts({
            response,
            successMessage: "Tipo de licença atualizada com sucesso",
         });
         return response;
      } catch (error) {
         console.log(error);
         ResponseAlerts({
            response: error.response,
            errorMessage: error.response?.data?.message ?? "Erro ao atualizar tipo",
         });
         return error.response;
      }
   }

   static async deleteType(id) {
      try {
         const response = await axios.delete(RESOURCES.DELETE_TYPE(id));
         ResponseAlerts({
            response,
            successMessage: "Tipo de licença removida com sucesso",
         });
         return response;
      } catch (error) {
         console.log(error);
         ResponseAlerts({
            response: error.response,
            errorMessage: error.response?.data?.message ?? "Erro ao remover tipo",
         });
         return error.response;
      }
   }

   static async getPaginatedFeatures(page, searchText, orderBy, includePrices = false) {
      try {
         return await axios.get(
            RESOURCES.PAGINATED_FEATURES(page, searchText ?? '', orderBy, includePrices),
         );
      } catch (error) {
         console.log(error);
         ResponseAlerts({
            response: error.response,
            errorMessage: error.response?.data?.message ?? "Erro ao listar funcionalidades paginado",
         });
         return error.response;
      }
   }

   static async getFeatures() {
      try {
         return await axios.get(
            RESOURCES.GET_FEATURES(),
         );
      } catch (error) {
         console.log(error);
         ResponseAlerts({
            response: error.response,
            errorMessage: error.response?.data?.message ?? "Erro ao listar funcionalidades",
         });
         return error.response;
      }
   }

   static async getPaginatedFeaturePrices(page, searchText, orderBy, featureId, period) {
      try {
         return await axios.get(
            RESOURCES.PAGINATED_FEATURE_PRICES(page, searchText ?? '', orderBy, featureId, period),
         );
      } catch (error) {
         console.log(error);
         ResponseAlerts({
            response: error.response,
            errorMessage: error.response?.data?.message ?? "Erro ao listar preços de funcionalidade paginado",
         });
         return error.response;
      }
   }

   static async getFeature(id) {
      try {
         return await axios.get(
            RESOURCES.GET_FEATURE(id),
         );
      } catch (error) {
         console.log(error);
         ResponseAlerts({
            response: error.response,
            errorMessage: error.response?.data?.message ?? "Erro ao obter funcionalidade",
         });
         return error.response;
      }
   }

   static async createFeature(payload) {
      try {
         const response = await axios.post(RESOURCES.CREATE_FEATURE(), payload);
         ResponseAlerts({
            response,
            successMessage: "Funcionalidade de licença criado com sucesso",
         });
         return response;
      } catch (error) {
         console.log(error);
         ResponseAlerts({
            response: error.response,
            errorMessage: error.response?.data?.message ?? "Erro ao criar funcionalidade",
         });
         return error.response;
      }
   }

   static async updateFeature(id, payload) {
      try {
         const response = await axios.put(RESOURCES.UPDATE_FEATURE(id), payload);
         ResponseAlerts({
            response,
            successMessage: "Funcionalidade de licença atualizada com sucesso",
         });
         return response;
      } catch (error) {
         console.log(error);
         ResponseAlerts({
            response: error.response,
            errorMessage: error.response?.data?.message ?? "Erro ao atualizar funcionalidade",
         });
         return error.response;
      }
   }

   static async deleteFeature(id) {
      try {
         const response = await axios.delete(RESOURCES.DELETE_FEATURE(id));
         ResponseAlerts({
            response,
            successMessage: "Funcionalidade de licença removida com sucesso",
         });
         return response;
      } catch (error) {
         console.log(error);
         ResponseAlerts({
            response: error.response,
            errorMessage: error.response?.data?.message ?? "Erro ao remover funcionalidade",
         });
         return error.response;
      }
   }

   static async getPaginatedDiscountReasons(page, searchText, orderBy) {
      try {
         return await axios.get(
            RESOURCES.PAGINATED_DISCOUNT_REASONS(page, searchText ?? '', orderBy),
         );
      } catch (error) {
         console.log(error);
         ResponseAlerts({
            response: error.response,
            errorMessage: error.response?.data?.message ?? "Erro ao listar motivos de descontos paginado",
         });
         return error.response;
      }
   }

   static async getDiscountReasons() {
      try {
         return await axios.get(
            RESOURCES.GET_DISCOUNT_REASONS(),
         );
      } catch (error) {
         console.log(error);
         ResponseAlerts({
            response: error.response,
            errorMessage: error.response?.data?.message ?? "Erro ao obter os motivos de desconto",
         });
         return error.response;
      }
   }


   static async getDiscountReason(id) {
      try {
         return await axios.get(
            RESOURCES.GET_DISCOUNT_REASON(id),
         );
      } catch (error) {
         console.log(error);
         ResponseAlerts({
            response: error.response,
            errorMessage: error.response?.data?.message ?? "Erro ao obter motivo de desconto",
         });
         return error.response;
      }
   }

   static async createDiscountReason(payload) {
      try {
         const response = await axios.post(RESOURCES.CREATE_DISCOUNT_REASON(), payload);
         ResponseAlerts({
            response,
            successMessage: "Motivo de desconto criado com sucesso",
         });
         return response;
      } catch (error) {
         console.log(error);
         ResponseAlerts({
            response: error.response,
            errorMessage: error.response?.data?.message ?? "Erro ao criar motivo de desconto",
         });
         return error.response;
      }
   }

   static async updateDiscountReason(id, payload) {
      try {
         const response = await axios.put(RESOURCES.UPDATE_DISCOUNT_REASON(id), payload);
         ResponseAlerts({
            response,
            successMessage: "Motivo de desconto atualizada com sucesso",
         });
         return response;
      } catch (error) {
         console.log(error);
         ResponseAlerts({
            response: error.response,
            errorMessage: error.response?.data?.message ?? "Erro ao atualizar motivo de desconto",
         });
         return error.response;
      }
   }

   static async deleteDiscountReason(id) {
      try {
         const response = await axios.delete(RESOURCES.DELETE_DISCOUNT_REASON(id));
         ResponseAlerts({
            response,
            successMessage: "Motivo de desconto removida com sucesso",
         });
         return response;
      } catch (error) {
         console.log(error);
         ResponseAlerts({
            response: error.response,
            errorMessage: error.response?.data?.message ?? "Erro ao remover motivo de desconto",
         });
         return error.response;
      }
   }

   static async updateSerialNumber(licenseId, serialNumber) {
      try {
         const response = await axios.post(
            RESOURCES.UPDATE_SERIAL_NUMBER(licenseId),
            {
               serialNumber,
            },
         );

         ResponseAlerts({
            response,
            successMessage: "Iniciado processo de troca do número de série",
         });

         return response;
      } catch (error) {
         console.log(error);

         ResponseAlerts({
            response: error.response,
            errorMessage:
               error.response?.data?.message ??
               "Erro ao atualizar número de série",
         });

         return error.response;
      }
   }

   static async transferToUnit(licenseId, unitId, reason) {
      try {
         const response = await axios.post(
            RESOURCES.TRANSFER_TO_UNIT(licenseId),
            {
               unitId,
               reason,
            },
         );

         ResponseAlerts({
            response,
            successMessage: "Licença transferida com sucesso",
         });

         return response;
      } catch (error) {
         console.log(error);

         ResponseAlerts({
            response: error.response,
            errorMessage:
               error.response?.data?.message ??
               "Erro ao transferir licença",
         });

         return error.response;
      }
   }

   static async updateAllocation(licenseId, unitId) {
      try {
         const response = await axios.post(
            RESOURCES.UPDATE_ALLOCATION(licenseId),
            {
               unitId,
            },
         );

         ResponseAlerts({
            response,
            successMessage: "Alocação da licença atualizada com sucesso",
         });

         return response;
      } catch (error) {
         console.log(error);

         ResponseAlerts({
            response: error.response,
            errorMessage:
               error.response?.data?.message ??
               "Erro ao atualizar alocação da licença",
         });

         return error.response;
      }
   }

   static async block(licenseId, payload) {
      try {
         const response = await axios.post(
            RESOURCES.BLOCK(licenseId),
            payload,
         );

         ResponseAlerts({
            response,
            successMessage: "Bloqueio da licença solicitado com sucesso",
         });

         return response;
      } catch (error) {
         console.log(error);

         ResponseAlerts({
            response: error.response,
            errorMessage:
               error.response?.data?.message ??
               "Erro ao bloquear licença",
         });

         return error.response;
      }
   }

   static async getStatesCount() {
      try {
         const response = await axios.get(
            RESOURCES.GET_STATES_COUNT(),
         );

         return response;
      } catch (error) {
         console.log(error);

         ResponseAlerts({
            response: error.response,
            errorMessage:
               error.response?.data?.message ??
               "Erro ao contabilizar estados das licenças",
         });

         return error.response;
      }
   }

   static async updateDueDate(licenseId, newDueDate) {
      throw new Error(
         "Endpoint para atualização da data de vencimento não foi informado."
      );
   }

   static async createBilling(payload) {
      try {
         const response = await axios.post(
            RESOURCES.CREATE_BILLING(),
            payload,
         );

         ResponseAlerts({
            response,
            successMessage: "Cobrança criada com sucesso",
         });

         return response;
      } catch (error) {
         console.log(error);

         ResponseAlerts({
            response: error.response,
            errorMessage:
               error.response?.data?.message ??
               "Erro ao criar cobrança",
         });

         return error.response;
      }
   }

   static async registerPayment(billingId, payload) {
      try {
         const response = await axios.post(
            RESOURCES.REGISTER_PAYMENT(billingId),
            payload,
         );

         ResponseAlerts({
            response,
            successMessage: "Pagamento registrado com sucesso",
         });

         return response;
      } catch (error) {
         console.log(error);

         ResponseAlerts({
            response: error.response,
            errorMessage:
               error.response?.data?.message ??
               "Erro ao registrar pagamento",
         });

         return error.response;
      }
   }

   static async updateBindingCode(licenseId, bindingCode) {
      try {
         const response = await axios.post(
            RESOURCES.UPDATE_BINDING_CODE(licenseId),
            { bindingCode },
         );

         ResponseAlerts({
            response,
            successMessage: "Iniciado processo de troca do código de vinculação",
         });

         return response;
      } catch (error) {
         console.log(error);

         ResponseAlerts({
            response: error.response,
            errorMessage:
               error.response?.data?.message ??
               "Erro ao atualizar código de vinculação",
         });

         return error.response;
      }
   }

   static async unblock(licenseId, payload) {
      try {
         const response = await axios.post(
            RESOURCES.UNBLOCK(licenseId),
            payload,
         );

         ResponseAlerts({
            response,
            successMessage: "Desbloqueio da licença solicitado com sucesso",
         });

         return response;
      } catch (error) {
         console.log(error);

         ResponseAlerts({
            response: error.response,
            errorMessage:
               error.response?.data?.message ??
               "Erro ao desbloquear licença",
         });

         return error.response;
      }
   }

   static async suspend(licenseId, payload) {
      try {
         const response = await axios.post(
            RESOURCES.SUSPEND(licenseId),
            payload,
         );

         ResponseAlerts({
            response,
            successMessage: "Suspensão da licença solicitada com sucesso",
         });

         return response;
      } catch (error) {
         console.log(error);

         ResponseAlerts({
            response: error.response,
            errorMessage:
               error.response?.data?.message ??
               "Erro ao suspender licença",
         });

         return error.response;
      }
   }

   static async renameLicense(licenseId, tag) {
      try {
         const response = await axios.post(
            RESOURCES.RENAME(licenseId),
            { tag },
         );

         ResponseAlerts({
            response,
            successMessage: "Licença renomeada com sucesso",
         });

         return response;
      } catch (error) {
         console.log(error);

         ResponseAlerts({
            response: error.response,
            errorMessage:
               error.response?.data?.message ??
               "Erro ao renomear licença",
         });

         return error.response;
      }
   }

   static async changeLicenseFeatures(licenseId, payload) {
      try {
         const response = await axios.post(
            RESOURCES.CHANGE_LICENSE_FEATURES(licenseId),
            payload,
         );

         ResponseAlerts({
            response,
            successMessage: "Funcionalidades da licença atualizadas com sucesso",
         });

         return response;
      } catch (error) {
         console.log(error);

         ResponseAlerts({
            response: error.response,
            errorMessage:
               error.response?.data?.message ??
               "Erro ao atualizar funcionalidades da licença",
         });

         return error.response;
      }
   }

   static async getAnalytics() {
      try {
         return await axios.get(
            RESOURCES.GET_ANALYTICS(),
         );
      } catch (error) {
         console.log(error);

         ResponseAlerts({
            response: error.response,
            errorMessage:
               error.response?.data?.message ??
               "Erro ao obter indicadores das licenças",
         });

         return error.response;
      }
   }

   static async updateDueDate(billingId, licenseId, dueDate) {
      try {
         const response = await axios.put(
            `${RESOURCES.UPDATE_DUE_DATE()}/${billingId}`,
            {
               billingId,
               licenseId,
               dueDate,
            },
         );

         ResponseAlerts({
            response,
            successMessage: "Data de vencimento atualizada com sucesso",
         });

         return response;
      } catch (error) {
         console.log(error);

         ResponseAlerts({
            response: error.response,
            errorMessage:
               error.response?.data?.message ??
               "Erro ao atualizar data de vencimento",
         });

         return error.response;
      }
   }
}

export default LicenseService;